/**
 * Pure helpers for `TruckRouteScheduleTab.tsx` (Task 7's Route Schedule tab),
 * kept dependency-free (no React/MUI/store imports) so they can be unit
 * tested directly in this repo's Node-only vitest environment without
 * needing a DOM. Mirrors the pattern established by
 * `pages/transportation/truckDetails.helpers.ts` (Task 6).
 */

/**
 * Mirrors the backend's `DayOfWeek` enum values (see `types/routeSchedule.type.ts`)
 * -- duplicated rather than imported so this module stays at zero imports;
 * the backend's day-of-week set is stable.
 */
export type ScheduleDayOfWeek =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

/**
 * The schedule tab's 7 columns, Sunday-first -- independent of the backend
 * enum's Monday-first internal value order (`DayOfWeek`'s declaration order
 * mirrors Python's `date.weekday()`, which is Monday-first).
 */
export const SCHEDULE_DAY_DISPLAY_ORDER: ScheduleDayOfWeek[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

/** Fully-qualified i18next key for a day's column label. */
export function resolveScheduleDayLabelKey(day: ScheduleDayOfWeek): string {
  return `transportation.fleet.details.schedule.days.${day}`;
}

/**
 * Looks up the schedule entry (if any) assigned to `day` within `days`. A day
 * absent from `days` is empty -- the backend never sends a null-route entry
 * for an unscheduled day, so "absent" is the only empty representation.
 */
export function findScheduleDayEntry<T extends { day_of_week: ScheduleDayOfWeek }>(
  days: readonly T[],
  day: ScheduleDayOfWeek,
): T | null {
  return days.find((entry) => entry.day_of_week === day) ?? null;
}

/**
 * All schedule entries for `day`, in their intended order. The backend
 * guarantees `days` is sorted by `(day_of_week, sequence)`, so entries for
 * the same day already sit adjacent and in order -- this only filters, it
 * never re-sorts. A day with zero scheduled routes returns an empty array,
 * never undefined. Unlike `findScheduleDayEntry` (which assumes at most one
 * entry per day), this is the grouping helper for multi-route days (Task 3:
 * 2+ ordered routes per weekday) -- callers that need "this day's ordered
 * `Route[]`" map this result's entries to `.route`.
 */
export function findScheduleDayEntries<T extends { day_of_week: ScheduleDayOfWeek }>(
  days: readonly T[],
  day: ScheduleDayOfWeek,
): T[] {
  return days.filter((entry) => entry.day_of_week === day);
}

export type ScheduleCellState = 'empty' | 'filled';

/** Derives whether a given day's cell should render as empty or filled. */
export function resolveScheduleCellState<T extends { day_of_week: ScheduleDayOfWeek }>(
  days: readonly T[],
  day: ScheduleDayOfWeek,
): ScheduleCellState {
  return findScheduleDayEntry(days, day) ? 'filled' : 'empty';
}

export type ScheduleTabViewState = 'loading' | 'error' | 'ready';

/**
 * Whether a loaded schedule actually belongs to the truck currently on
 * screen. `useRouteScheduleStore` is a module-level singleton: switching from
 * one truck's schedule tab to another's (e.g. via `FleetManagement.tsx`'s
 * client-side `navigate()`) leaves the *previous* truck's `schedule` sitting
 * in state while the new truck's `fetchTruckSchedule` is in flight. Without
 * this check, that stale schedule reads as "already loaded" for the new
 * truck too.
 */
export function scheduleMatchesTruck(scheduleTruckId: string | null, truckId: string): boolean {
  return scheduleTruckId !== null && scheduleTruckId === truckId;
}

/**
 * Picks which of the tab's whole-panel states applies: a skeleton while the
 * initial fetch for *this* truck is in flight (unless this truck's schedule
 * is already loaded, e.g. a background refetch), an Alert+retry if that
 * fetch failed outright with nothing of this truck's loaded yet, otherwise
 * the real grid. Truck-identity-aware (see `scheduleMatchesTruck`) so a
 * different truck's already-loaded schedule is never mistaken for this
 * truck's -- mirrors `resolveTruckDetailsViewState`'s `truckMatchesParam`
 * check in `truckDetails.helpers.ts`.
 */
export function resolveScheduleTabViewState(args: {
  isLoading: boolean;
  hasError: boolean;
  /** The truck currently being displayed. */
  truckId: string;
  /** `schedule?.truck_id ?? null` -- which truck the loaded schedule (if any) actually belongs to. */
  scheduleTruckId: string | null;
}): ScheduleTabViewState {
  const { isLoading, hasError, truckId, scheduleTruckId } = args;
  const hasMatchingSchedule = scheduleMatchesTruck(scheduleTruckId, truckId);
  if (isLoading && !hasMatchingSchedule) return 'loading';
  if (hasError && !hasMatchingSchedule) return 'error';
  return 'ready';
}

/**
 * Minimal shape of a `Route`'s `assigned_truck` field (see `types/route.type.ts`'s
 * `RouteTruckSummary`) -- duplicated rather than imported so this module stays at
 * zero imports.
 */
export interface ScheduleRouteAssignedTruck {
  id: string;
  name: string;
}

/**
 * Minimal shape of the `Route` fields `resolveMismatchedAssignedTruck` reads --
 * duplicated rather than imported so this module stays at zero imports.
 */
export interface ScheduleRouteForAssignment {
  assigned_truck: ScheduleRouteAssignedTruck | null;
}

/**
 * A route scheduled onto this truck's week can still carry a *different*
 * truck as its own `assigned_truck` (that field reflects the route's
 * ownership/assignment elsewhere, independent of which truck's schedule it's
 * appearing on here). Returns that other truck so `ScheduledRouteCard` can
 * surface a "also assigned to" note -- null when there's nothing to flag
 * (no assigned truck, or it's this same viewer truck).
 */
export function resolveMismatchedAssignedTruck(
  route: ScheduleRouteForAssignment,
  viewerTruckId: string,
): ScheduleRouteAssignedTruck | null {
  const { assigned_truck: assignedTruck } = route;
  if (!assignedTruck || assignedTruck.id === viewerTruckId) return null;
  return assignedTruck;
}

/**
 * The Sunday-Saturday span containing `referenceDate` (defaults to now) --
 * purely a display label for "which calendar week is this recurring
 * template currently standing in for," matching `SCHEDULE_DAY_DISPLAY_ORDER`'s
 * Sunday-first convention. The schedule itself has no date dimension (a
 * truck's Monday route is always Route X until changed, not tied to any one
 * week) -- this never touches the data model, it's presentation only.
 */
export function getCurrentWeekRange(referenceDate: Date = new Date()): { start: Date; end: Date } {
  const start = new Date(referenceDate);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - start.getDay());
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return { start, end };
}

/**
 * The actual calendar date for `day` within the week starting at `weekStart`
 * (a Sunday, e.g. from `getCurrentWeekRange`) -- offsets by `day`'s index in
 * `SCHEDULE_DAY_DISPLAY_ORDER`, so it stays correct even if that order ever
 * changes. Presentation only, same caveat as `getCurrentWeekRange`.
 */
export function getDateForDayInWeek(weekStart: Date, day: ScheduleDayOfWeek): Date {
  const date = new Date(weekStart);
  date.setDate(date.getDate() + SCHEDULE_DAY_DISPLAY_ORDER.indexOf(day));
  return date;
}

/**
 * Minimal shape of a `SupplierCollectionPoint` (see `types/supplier.type.ts`)
 * -- duplicated rather than imported so this module stays at zero imports.
 * `supplierName` is attached by the caller (a second join, against the org's
 * suppliers list) -- optional because it's only known once that second fetch
 * resolves, and a point whose supplier lookup failed should still render.
 */
export interface ScheduleCollectionPointSummary {
  id: string;
  name: string;
  supplierName?: string;
}

/**
 * Resolves a route's ordered `collection_point_ids` against an org-wide
 * points-by-id map into the actual point objects, in route order --
 * mirrors `RouteDetails.tsx`'s inline id-to-point join, extracted here so
 * `ScheduledRouteCard` can render each day's stop order without needing to
 * know how the map was built. An id with no matching point (e.g. deleted
 * since the route was built) is silently dropped, never a gap in the list.
 */
export function resolveOrderedCollectionPoints<T extends ScheduleCollectionPointSummary>(
  collectionPointIds: readonly string[],
  collectionPointsById: ReadonlyMap<string, T>,
): T[] {
  return collectionPointIds
    .map((id) => collectionPointsById.get(id))
    .filter((point): point is T => Boolean(point));
}

/** Minimal shape of a route's `start_facility`/`end_facility` (see `types/route.type.ts`'s `RouteFacilitySummary`). */
export interface ScheduleTimelineFacility {
  id: string;
  name: string;
}

/** One stop along a route's full journey, rendered as a timeline node. */
export interface ScheduleTimelineNode {
  id: string;
  name: string;
  /** Set only for an actual collection-point stop, never for a start/end facility node. */
  supplierName?: string;
}

/**
 * The route's full journey as one ordered sequence -- start facility (if
 * any), then every collection point in route order, then the end facility
 * (if any). A route timeline should read as one continuous trip, not a
 * separate "start -> end" line plus a disconnected stop list.
 */
export function buildRouteTimeline(
  startFacility: ScheduleTimelineFacility | null,
  collectionPoints: readonly ScheduleCollectionPointSummary[],
  endFacility: ScheduleTimelineFacility | null,
): ScheduleTimelineNode[] {
  const nodes: ScheduleTimelineNode[] = [];
  if (startFacility) nodes.push({ id: startFacility.id, name: startFacility.name });
  nodes.push(...collectionPoints);
  if (endFacility) nodes.push({ id: endFacility.id, name: endFacility.name });
  return nodes;
}
